import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import cn from 'classnames';
import { Page } from 'Components';
import { AppContext } from 'Context';
import { Pathname } from 'Routes';
import { isNewUIEnabled } from 'hooks/useNewUI';
import PillNav from 'Components/reactbits/PillNav';
import { UserRoundPlus, Copy, Check } from 'Utils/lucideIcons';
import styles from './index.module.css';

// Viewers / Groups / Successors / Connected Accounts are sibling routes; the
// PillNav switches between them (active tab derived from the current path).
const TAB_ROUTES = [
  Pathname.getViewersPath(Pathname.viewers.viewer),
  Pathname.getViewersPath(Pathname.viewers.groups),
  Pathname.getViewersPath(Pathname.viewers.successors),
  Pathname.getViewersPath(Pathname.viewers.connectedAccounts),
];

/**
 * Chrome the active tab publishes upward. Only primitives travel through state
 * (safe as effect deps); the handlers live in a ref so a fresh closure on every
 * child render cannot loop this component.
 */
export const ViewerChromeContext = createContext(null);

const DEFAULT_CHROME = {
  title: '',
  subTitle: undefined,
  addActionName: undefined,
  link: undefined,
  restricted: false,
  hideHeaderActions: false,
  showCopy: false,
};

/**
 * Section layout for /viewers/*. It owns <Page> — and therefore the app header
 * — so switching tabs swaps only the tab's content instead of tearing the
 * header down and remounting it (which replayed its whole load, glitching the
 * top bar on every switch). The PillNav stays mounted too, so its indicator
 * slides between tabs rather than re-entering from scratch.
 */
export const ViewersLayout = () => {
  const { langText, setTopOrBottom } = useContext(AppContext);
  const navigate = useNavigate();
  const location = useLocation();

  const [chrome, setChrome] = useState(DEFAULT_CHROME);
  const handlers = useRef({});
  const [copied, setCopied] = useState(false);

  // Mounted once for the whole section rather than per tab, so the sidebar's
  // top/bottom anchoring no longer flips off and on with every switch.
  useEffect(() => {
    setTopOrBottom(true);
    return () => { setTopOrBottom(false); };
  }, []);

  const handleCopyClick = () => {
    handlers.current.onCopyClick?.();
    setCopied(true);
    setTimeout(() => setCopied(false), 1000);
  };

  const sideBarText = langText?.viewersSideBar;
  const activeIndex = Math.max(0, TAB_ROUTES.indexOf(location.pathname));
  const {
    title, subTitle, addActionName, link, restricted, hideHeaderActions, showCopy,
  } = chrome;
  const subtext = subTitle
    || 'Manage who can watch your projector — invite viewers, organize them into groups, and set successors.';

  return (
    <Page
      contentClassName={restricted ? styles.pageContentRes : styles.pageContent}
      headerTheme={styles.headerTheme}
    >
      <div className={styles.viewerPage}>
        {/* Header: title + subtext. The action buttons live down in the tab row
            so the primary action sits on the same line as the pill tabs. */}
        <div className={styles.viewerHead}>
          <div className={styles.titleBlock}>
            <h1 className={cn(styles.pageTitle, { [styles.restrictedPageTitle]: restricted })}>
              {title}
            </h1>
            <p className={styles.pageSub}>{subtext}</p>
          </div>
        </div>

        {/* Pill tab bar (route-driven) + the active tab's actions, on one line. */}
        <div className={styles.tabBar}>
          <PillNav
            items={[
              sideBarText?.viewersSidebarTitle || 'Viewers',
              sideBarText?.groupSidebarTitle || 'Groups',
              sideBarText?.successorSidebarTitle || 'Successors',
              'Connected Accounts',
            ]}
            activeIndex={activeIndex}
            onChange={(i) => navigate(TAB_ROUTES[i])}
            accent="#15171c"
          />
          <div className={styles.tabBarRight}>
            {!hideHeaderActions && (
              <div className={styles.headerActions}>
                <button
                  type="button"
                  className={cn(styles.inviteBtn, { [styles.disabledbutton]: restricted })}
                  onClick={() => {
                    if (restricted) return;
                    // Pages that own their own creation modal publish a handler;
                    // the rest still navigate to a `?…` search-param route.
                    if (handlers.current.onAddAction) handlers.current.onAddAction();
                    else if (link) navigate(link);
                  }}
                  disabled={restricted}
                >
                  <UserRoundPlus size={17} strokeWidth={2} />
                  <span>{addActionName || 'Invite'}</span>
                </button>
                {showCopy && (
                  <button
                    type="button"
                    className={cn(styles.copyLinkBtn, { [styles.copyLinkBtnCopied]: copied })}
                    onClick={handleCopyClick}
                    title="Copy invite link"
                  >
                    {copied ? <Check size={16} strokeWidth={2.4} /> : <Copy size={16} strokeWidth={2} />}
                    <span>{copied ? 'Copied' : 'Copy link'}</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Only this swaps when a tab changes. */}
        <div className={styles.content}>
          <ViewerChromeContext.Provider value={{ setChrome, handlers }}>
            <Outlet />
          </ViewerChromeContext.Provider>
        </div>
      </div>
    </Page>
  );
};

/**
 * The /viewers routes serve both UIs. Classic pages bring their own PageClassic
 * chrome, so they render straight through with no shared layout.
 */
export const ViewersLayoutSwitch = () => (isNewUIEnabled() ? <ViewersLayout /> : <Outlet />);

export default ViewersLayoutSwitch;
